import {
  AddCircleRounded,
  AspectRatio,
  CloseFullscreen,
  Delete,
  Edit,
  PriorityHigh,
  Restore,
  Undo,
} from "@mui/icons-material";
import {
  Autocomplete,
  Card,
  CircularProgress,
  Grid,
  Icon,
  IconButton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import { makeStyles } from "@mui/styles";
import fetchSingleCustomer from "api/companies/fetchSingleCustomer";
import ObjectId from "bson-objectid";
import ConfirmationModal from "components/ConfirmationModal";
import DataTableWithSubrows from "components/DataTableWithSubrows";
import CustomTextField from "components/Form/CustomTextField";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { defaultReportModalValues } from "components/ReportModal/fixtures";
import SplitPanelSelector from "components/SplitPanelSelector";
import { useAppContextController } from "context/AppContext";
import useHandleBackButtonSubGrid from "hooks/useHandleBackButtonSubGrid";
import useSessionAuth from "hooks/useSessionAuth";
import fetchSingleEvent from "layouts/pages/events/actions/fetchSingleEvent";
import fetchSingleJob from "layouts/pages/jobs/actions/fetchSingleJob";
import fetchSingleVenueFromSlug from "layouts/pages/venues/actions/fetchSingleVenueFromSlug";
import { cloneDeep, isArray } from "lodash";
import moment from "moment";
import { useSnackbar } from "notistack";
import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { CommandManager } from "utils/classes/commandManager";
import { SaveInvoiceDataCommand } from "utils/classes/saveInvoiceDataCommand";
import { calculateInvoiceSummary } from "utils/helpers/invoiceHelper";
import useArrowKeysFieldSelection from "utils/useArrowKeysFieldSelection";
import useSort from "utils/useSort";
import createInvoiceBatch from "../../actions/createInvoiceBatch";
import deleteInvoiceBatch from "../../actions/deleteInvoiceBatch";
import fetchInvoiceOptions from "../../actions/fetchInvoiceOptions";
import fetchInvoices from "../../actions/fetchInvoices";
import getPayrollStatus from "../../actions/getPayrollStatus";
import resetInvoiceBatch from "../../actions/resetInvoiceBatch";
import submitTimecardBatch from "../../actions/submitTimecardBatch";
import updateInvoiceBatch from "../../actions/updateInvoiceBatch";
import AddInvoiceRowModal from "../AddInvoiceRowModal";
import EditableCellNumber from "../EditableCellNumber";
import EditInvoiceCustomerDataModal from "../EditInvoiceCustomerDataModal";
import SendInvoiceMessageModal from "../SendInvoiceMessageModal";
import InvoiceEditorPreviewModal from "./components/InvoiceEditorPreviewModal";
import InvoiceEditorTableActions from "./components/InvoiceEditorTableActions";

const useStyle = makeStyles({
  box: {
    overflow: "visible!important",
    paddingBottom: 0,
  },
  addButton: {
    fontSize: 40,
  },
});

const CustomWidthTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: 500,
    whiteSpace: "pre-line",
  },
});

const defaultMinimizedPageSize = 10;

const InvoiceEditorTable = ({
  title = "Invoice Editor",
  pageSize = 5,
  setFilters,
  navigateToAction = "",
  parentPage,
  parentFilters,
  parentNavigateWithBack,
  currentPeriod,
  fullscreen,
  panelOptions,
  burdenRate,
  parentInvoiceId,
  parentEventUrl,
  parentJobSlug,
}) => {
  const queryClient = useQueryClient();
  const { action, id, eventUrl, jobSlug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const setNavigateUrl = useNavigate();
  const [genericModalOpen, setGenericModalOpen] = useState(false);
  const classes = useStyle();
  const { currentVenue, venues, userType, currentLoggedUser, currentEvent, currentJob, company } =
    useAppContextController();

  const eventUrlFinal = parentEventUrl || eventUrl;
  const jobSlugFinal = parentJobSlug || jobSlug;
  const isForInvoicesScreen = !!parentInvoiceId;

  const paramInvoiceId = searchParams.get("invoiceId");

  const isShiftJob = !!jobSlugFinal;

  const allSearchParams = useMemo(() => {
    const allParams = {};
    const paramsArray = [];
    searchParams.entries().forEach(([key, value]) => {
      // Exclude "panel" query param
      if (key === "panel" || key === "invoiceId") return;

      if (searchParams.getAll(key).length > 1) {
        allParams[key] = encodeURIComponent(searchParams.getAll(key));
      } else {
        allParams[key] = encodeURIComponent(value);
      }
      paramsArray.push(`${key}=${allParams[key]}`);
    });
    return paramsArray;
  }, [searchParams]);

  const [isBulkMessageModalOpen, toggleBulkMessageModal] = useState(false);
  const [isStaffingPoolModalOpen, toggleStaffingPoolModalOpen] = useState(false);
  const [openReportMessageModal, setOpenReportMessageModal] = useState(false);
  const [tableQueryDetails, setTableQueryDetails] = useState(null);
  const [chartQueryDetails, setChartQueryDetails] = useState(null);
  const [tableReportData, setTableReportData] = useState(null);
  const [chartReportData, setChartReportData] = useState(null);
  const [reportModalValues, setReportModalValues] = useState(defaultReportModalValues);
  const [commandManager, setCommandManager] = useState(() => new CommandManager());
  const [isPartnerMembersModalOpen, setPartnerMembersModalOpen] = useState(false);
  const [isResetConfirmationModalOpen, setResetConfirmationModalOpen] = useState(false);
  const [isDeleteConfirmationModalOpen, setDeleteConfirmationModalOpen] = useState(false);
  const [isEditCustomerInfoModalOpen, setEditCustomerInfoModalOpen] = useState(false);
  const [isEditInvoiceInfoModalOpen, setEditInvoiceInfoModalOpen] = useState(false);
  const [isEditRemittanceInfoModalOpen, setEditRemittanceInfoModalOpen] = useState(false);
  const [selectedRowForEdit, setSelectedRowForEdit] = useState(null);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [isShowingDetailsView, setShowingDetailsView] = useState(false);
  const [invoicePreviewOpen, setInvoicePreviewOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(paramInvoiceId);
  const [invoiceFile, setInvoiceFile] = useState(null);
  const { enqueueSnackbar } = useSnackbar();

  const { logoutAuth0User } = useSessionAuth();

  // Pagination state
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10); // Default pageSize higher than 10. Don't use preferred pageSize

  const navigate = useNavigate();

  // If invoiceId is provided, then we should fetch the event or job instead of using the one in the context
  const {
    data: fetchedEvent,
    isLoading: isLoadingFetchedEvent,
    refetch: refetchEvent,
  } = useQuery(
    ["invoiceEvent", { eventUrl: parentEventUrl }],
    () => fetchSingleEvent({ eventUrl: parentEventUrl }),
    {
      enabled: !!parentInvoiceId && !!parentEventUrl,
    }
  );

  const {
    data: fetchedJob,
    isLoading: isLoadingFetchedJob,
    refetch: refetchJob,
  } = useQuery(["invoiceJob", { jobSlug: parentJobSlug }], () => fetchSingleJob(parentJobSlug), {
    enabled: !!parentInvoiceId && !!parentJobSlug,
  });

  const currentEventFinal = fetchedEvent || currentEvent;
  const currentJobFinal = fetchedJob?.data?.job || currentJob;
  const selectedInvoiceFinal = parentInvoiceId || selectedInvoice;

  const d = new Date();
  d.setDate(d.getDate() + 2);
  const today = d.toISOString().split("T")[0];
  const eventDay = new Date(currentEventFinal?.eventDate).getTime() <= new Date(today).getTime();

  const toggleFullscreen = (e) => {
    e.stopPropagation();
    setPage(1);
    navigate(
      `/payroll/${isShiftJob ? "job" : "event"}/${
        isShiftJob ? currentJobFinal?.jobSlug : currentEventFinal?.eventUrl
      }/action/invoice?invoiceId=${selectedInvoiceFinal ?? ""}${
        allSearchParams?.length ? `&${allSearchParams.join("&")}` : ""
      }`
    );
  };

  const minimize = (e) => {
    e.stopPropagation();
    setPage(1);
    navigate(
      `/payroll/${isShiftJob ? "job" : "event"}/${
        isShiftJob ? currentJobFinal?.jobSlug : currentEventFinal?.eventUrl
      }/action/${isShiftJob ? "jobinfo" : "eventsinfo"}?panel=invoice&invoiceId=${
        selectedInvoiceFinal ?? ""
      }${allSearchParams?.length ? `&${allSearchParams.join("&")}` : ""}`
    );
  };

  // Handle back button from parent screens
  const { navigateWithBack } = useHandleBackButtonSubGrid({
    parentPage,
    parentFilters,
    setPage,
    setFilters: () => {},
    page,
    filters: {},
    navigateFunc: setNavigateUrl,
    navigateWithBackFunc: parentNavigateWithBack,
  });

  const handlePanelChange = useCallback(
    (e, panel) => {
      e.stopPropagation();
      if (["timecard", "payroll", "invoice"].includes(panel)) {
        navigate(
          `/payroll/${isShiftJob ? "job" : "event"}/${
            isShiftJob ? currentJobFinal?.jobSlug : currentEventFinal?.eventUrl
          }/action/${panel}${allSearchParams?.length ? `?${allSearchParams.join("&")}` : ""}`
        );
        return;
      }
      navigate(
        `/payroll/${isShiftJob ? "job" : "event"}/${
          isShiftJob ? currentJobFinal?.jobSlug : currentEventFinal?.eventUrl
        }/action/${isShiftJob ? "jobinfo" : "eventsinfo"}?panel=${panel}${
          allSearchParams?.length ? `&${allSearchParams.join("&")}` : ""
        }`
      );
    },
    [allSearchParams, currentEventFinal?.eventUrl, currentJobFinal?.jobSlug, isShiftJob, navigate]
  );

  const localPanelOptions = useMemo(() => {
    return panelOptions?.map((op) => ({
      ...op,
      hasShadow: op.name === "status" ? "emd" : "",
      fn: (e) => (op.name === "status" ? () => {} : handlePanelChange(e, op.name)),
    }));
  }, [handlePanelChange, panelOptions]);

  const { order, orderBy, toggleSort } = useSort("lastName", "asc");
  const options = useMemo(
    () => ({ page, limit: fullscreen ? limit : defaultMinimizedPageSize, order, orderBy }),
    [fullscreen, limit, order, orderBy, page]
  );
  const allOptions = useMemo(
    () => ({
      ...options,
      ...(isShiftJob
        ? {
            jobSlug: currentJobFinal?.jobSlug,
            ...(currentPeriod?.length === 2
              ? {
                  startDate: currentPeriod[0].toISOString(),
                  endDate: currentPeriod[1].toISOString(),
                }
              : {}),
          }
        : {
            eventUrl: currentEventFinal?.eventUrl,
          }),
    }),
    [currentEventFinal?.eventUrl, currentJobFinal?.jobSlug, currentPeriod, isShiftJob, options]
  );

  const allOptionsInvoices = useMemo(
    () => ({
      fetchAll: true,
      order: "asc",
      orderBy: "name",
      filters: {
        ...(isShiftJob
          ? {
              jobSlug: currentJobFinal?.jobSlug,
              ...(currentPeriod?.length === 2
                ? {
                    startDate: currentPeriod[0].toISOString().split("T")[0],
                    endDate: currentPeriod[1].toISOString().split("T")[0],
                  }
                : {}),
            }
          : {
              eventUrl: currentEventFinal?.eventUrl,
            }),
      },
    }),
    [currentEventFinal?.eventUrl, currentJobFinal?.jobSlug, currentPeriod, isShiftJob]
  );

  const allOptionsPayrollStatus = useMemo(() => {
    return {
      ...(isShiftJob
        ? {
            jobSlug: currentJobFinal?.jobSlug,
            ...(currentPeriod?.length === 2
              ? {
                  startDate: currentPeriod[0].toISOString(),
                  endDate: currentPeriod[1].toISOString(),
                }
              : {}),
          }
        : {
            eventUrl: currentEventFinal?.eventUrl,
            ...(currentPeriod?.length === 2
              ? {
                  startDate: currentPeriod[0].toISOString(),
                  endDate: currentPeriod[1].toISOString(),
                }
              : {}),
          }),
    };
  }, [currentEventFinal?.eventUrl, currentJobFinal?.jobSlug, currentPeriod, isShiftJob]);

  useEffect(() => {
    return () => {
      // Clear all cached data
      queryClient.removeQueries({ queryKey: ["invoiceList", allOptions] });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    data: invoiceList,
    isLoading: isLoadingInvoiceList,
    refetch: refetchInvoiceList,
  } = useQuery(["invoiceList", allOptionsInvoices], () => fetchInvoices(allOptionsInvoices), {
    // cacheTime: 0,
    // staleTime: 0,
    enabled: isShiftJob ? !!currentJobFinal?.jobSlug : !!currentEventFinal?.eventUrl,
  });

  const {
    data: venueCustomer,
    isLoading: isLoadingVenueCustomer,
    refetch: refetchVenueCustomer,
  } = useQuery(
    ["venueCustomer", allOptionsInvoices],
    () =>
      company?.companyType === "Company"
        ? fetchSingleCustomer({
            slug: isShiftJob ? currentJobFinal?.companySlug : currentEventFinal?.companySlug,
          })
        : fetchSingleVenueFromSlug({
            slug: isShiftJob ? currentJobFinal?.venueSlug : currentEventFinal?.venueSlug,
          }),
    {
      enabled: isShiftJob ? !!currentJobFinal?.jobSlug : !!currentEventFinal?.eventUrl,
    }
  );

  const invoiceOptionsOptions = {
    eventUrl: currentEventFinal?.eventUrl,
    jobSlug: currentJobFinal?.jobSlug,
  };

  const {
    data: invoiceOptions,
    isLoading: isLoadingInvoiceOptions,
    refetch: refetchInvoiceOptions,
  } = useQuery(
    ["invoiceOptions", invoiceOptionsOptions],
    () => fetchInvoiceOptions(invoiceOptionsOptions),
    {
      enabled: isShiftJob ? !!currentJobFinal?.jobSlug : !!currentEventFinal?.eventUrl,
    }
  );

  useEffect(() => {
    if (selectedInvoiceFinal) {
      if (!invoiceList?.data?.length) {
        setSelectedInvoice("");
      } else {
        const found = invoiceList.data.find((item) => item._id === selectedInvoiceFinal);
        if (!found) {
          setSelectedInvoice("");
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceList]);

  const invoiceDropdownOptions = useMemo(() => {
    if (!invoiceList?.data?.length) return [];
    return invoiceList.data.map((inv) => ({
      label: `${inv.invoiceNumber} - ${inv.name}`,
      value: inv._id.toString(),
    }));
  }, [invoiceList?.data]);

  const selectedInvoiceData = useMemo(() => {
    if (!invoiceList?.data?.length || !selectedInvoiceFinal) return null;
    return invoiceList.data.find((inv) => inv._id === selectedInvoiceFinal);
  }, [invoiceList?.data, selectedInvoiceFinal]);

  const invoiceStatus = selectedInvoiceData?.status;

  const {
    data: payrollStatus,
    isLoading: isLoadingPayrollStatus,
    refetch: refetchPayrollStatus,
  } = useQuery(
    ["payrollStatus", allOptionsPayrollStatus],
    () => getPayrollStatus(allOptionsPayrollStatus),
    {
      enabled: !isForInvoicesScreen,
    }
  );

  const timecardSubmitted = isForInvoicesScreen
    ? true
    : payrollStatus?.data?.timecardStatus === "Submitted";
  const payrollSubmitted = isForInvoicesScreen
    ? true
    : payrollStatus?.data?.payrollStatus === "Submitted";
  const submitLocked = false;
  const editLocked = invoiceStatus === "Void";

  const stringifiedOptions = JSON.stringify(allOptions);
  useEffect(() => {
    setCommandManager(() => new CommandManager());
  }, [stringifiedOptions]);

  const resetDataMutation = useMutation(resetInvoiceBatch);
  const deleteDataMutation = useMutation(deleteInvoiceBatch);
  const updateDataMutation = useMutation(updateInvoiceBatch);
  const createMutation = useMutation(createInvoiceBatch);
  const submitDataMutation = useMutation(submitTimecardBatch, {
    onError: (e) => {
      const errorMessage =
        e?.response?.data?.message ?? "An error has occurred submitting the Timecard";
      enqueueSnackbar(errorMessage, { variant: "error" });
    },
  });

  const autosaveQueueRef = useRef([]);
  const isProcessingQueueRef = useRef(false);

  const calculatedSummary = useMemo(() => {
    return calculateInvoiceSummary({
      details: selectedInvoiceData?.details ?? [],
      isShiftJob,
      eventName: currentEventFinal?.eventName,
      jobName: currentJobFinal?.title,
      date: isShiftJob
        ? ""
        : moment(currentEventFinal?.eventDate)
            .tz(currentEventFinal?.timeZone || "America/Chicago")
            .format("MM/DD/YYYY"),
      timeZone: currentEventFinal?.timeZone,
    });
  }, [
    currentEventFinal?.eventDate,
    currentEventFinal?.eventName,
    currentEventFinal?.timeZone,
    currentJobFinal?.title,
    isShiftJob,
    selectedInvoiceData?.details,
  ]);

  // ------------ FIELD SWITCH ------------
  const inputRefs = useRef({});
  const gridDimensions = useMemo(
    () => ({
      rows:
        (isShowingDetailsView ? selectedInvoiceData?.details ?? [] : calculatedSummary ?? [])
          .length ?? 0,
      cols: 3,
    }),
    [calculatedSummary, isShowingDetailsView, selectedInvoiceData?.details]
  );
  const { getFieldKey, handleKeyDown: handleKeyDownField } = useArrowKeysFieldSelection({
    inputRefs,
    gridDimensions,
  });
  // --------------------------------------

  const handleSelectPartnerMembers = (row) => {
    setSelectedPartner(row);
    setPartnerMembersModalOpen(true);
  };

  const handleClosePartnerMembersModal = () => {
    setSelectedPartner(null);
    setPartnerMembersModalOpen(false);
  };

  // Update local cache of query data
  const updateDataCache = useCallback(
    (invoiceId, newData) => {
      const currentCacheData = queryClient.getQueryData(["invoiceList", allOptionsInvoices]);
      const dataCopy = cloneDeep(currentCacheData);

      const dataArray = dataCopy?.data?.map((invoice) => {
        if (invoice?._id === invoiceId) {
          return {
            ...invoice,
            ...newData,
          };
        }
        return invoice;
      });

      if (dataCopy?.data) dataCopy.data = dataArray;
      queryClient.setQueryData(["invoiceList", allOptionsInvoices], dataCopy);
    },
    [allOptionsInvoices, queryClient]
  );

  const pushChange = useCallback(
    async (requestData, originalData) => {
      const command = new SaveInvoiceDataCommand(
        autosaveQueueRef,
        isProcessingQueueRef,
        requestData,
        originalData,
        updateDataCache,
        updateDataMutation
      );
      commandManager.executeCommand(command);
    },
    [commandManager, updateDataCache, updateDataMutation]
  );

  // Undo function
  const handleUndo = useCallback(() => {
    if (commandManager.undo()) enqueueSnackbar("Last change undone", { variant: "success" });
    else enqueueSnackbar("Nothing to undo", { variant: "error" });
  }, [commandManager, enqueueSnackbar]);

  // Reset data function
  const handleReset = useCallback(async () => {
    const result = await resetDataMutation.mutateAsync(
      isShiftJob && currentJobFinal?.jobSlug
        ? {
            jobSlug: currentJobFinal?.jobSlug,
            data: {
              invoiceId: selectedInvoiceFinal,
            },
          }
        : {
            eventUrl: currentEventFinal?.eventUrl,
            data: {
              invoiceId: selectedInvoiceFinal,
            },
          }
    );
    if (result?.data?.success) {
      enqueueSnackbar("Invoice reset successfully", { variant: "success" });
      setCommandManager(() => new CommandManager());
      refetchInvoiceList();
      refetchPayrollStatus();
    } else {
      enqueueSnackbar("An error has occurred", { variant: "success" });
    }
  }, [
    currentEventFinal?.eventUrl,
    currentJobFinal?.jobSlug,
    enqueueSnackbar,
    isShiftJob,
    refetchInvoiceList,
    refetchPayrollStatus,
    resetDataMutation,
    selectedInvoiceFinal,
  ]);

  // Reset data function
  const handleDelete = useCallback(async () => {
    const result = await deleteDataMutation.mutateAsync({
      invoiceId: selectedInvoiceFinal,
    });
    if (result?.data?.success) {
      enqueueSnackbar("Invoice deleted successfully", { variant: "success" });
      setCommandManager(() => new CommandManager());
      refetchInvoiceList();
      refetchPayrollStatus();
      await queryClient.invalidateQueries(["invoices"]);
      setSelectedInvoice("");
    } else {
      enqueueSnackbar("An error has occurred", { variant: "success" });
    }
  }, [
    deleteDataMutation,
    enqueueSnackbar,
    queryClient,
    refetchInvoiceList,
    refetchPayrollStatus,
    selectedInvoiceFinal,
  ]);

  // Submit data function
  const handleCreate = useCallback(async () => {
    const result = await createMutation.mutateAsync(
      isShiftJob && currentJobFinal?.jobSlug
        ? {
            jobSlug: currentJobFinal?.jobSlug,
            data: {
              startDate: currentPeriod?.[0],
              endDate: currentPeriod?.[1],
              invoiceName: `${currentJobFinal?.title} - ${moment().format("YYYY-MM-DD")}`,
            },
          }
        : {
            eventUrl: currentEventFinal?.eventUrl,
            data: {
              startDate: currentPeriod?.[0],
              endDate: currentPeriod?.[1],
              invoiceName: `${currentEventFinal?.eventName} - ${moment().format("YYYY-MM-DD")}`,
            },
          }
    );
    if (result?.data?.success) {
      enqueueSnackbar("Invoice created successfully", { variant: "success" });
      setCommandManager(() => new CommandManager());

      // Select the new invoice
      const newInvoice = result.data.data;
      const currentCacheData = queryClient.getQueryData(["invoiceList", allOptionsInvoices]);
      const dataCopy = cloneDeep(currentCacheData);
      dataCopy.data.push(newInvoice);
      queryClient.setQueryData(["invoiceList", allOptionsInvoices], dataCopy);
      setSelectedInvoice(newInvoice._id);

      // refetchInvoiceList();
      refetchPayrollStatus();
    } else {
      enqueueSnackbar("An error has occurred", { variant: "success" });
    }
  }, [
    allOptionsInvoices,
    createMutation,
    currentEventFinal?.eventName,
    currentEventFinal?.eventUrl,
    currentJobFinal?.jobSlug,
    currentJobFinal?.title,
    currentPeriod,
    enqueueSnackbar,
    isShiftJob,
    queryClient,
    refetchPayrollStatus,
  ]);

  const onChangeField = useCallback(
    (row, fieldName, fieldValue) => {
      const currentData = { ...selectedInvoiceData };
      const updatedField = isShowingDetailsView ? "details" : "summary";

      const originalData = {
        [updatedField]: currentData[updatedField] ?? [],
      };

      let hasChanges = false;

      const newData = (currentData[updatedField] ?? []).map((item) => {
        if (item.itemId === row.itemId) {
          // Find if there's an edit for the current field
          const originalRow = {
            ...row,
          };

          const newRow = { ...originalRow };
          if (isArray(fieldName)) {
            for (let index = 0; index < fieldName.length; index += 1) {
              const fieldN = fieldName[index];
              const fieldV = fieldValue[index];

              newRow[fieldN] = fieldV;

              // eslint-disable-next-line eqeqeq
              if ((fieldV ?? "") != (originalRow[fieldN] ?? "")) hasChanges = true;
            }
          } else {
            newRow[fieldName] = fieldValue;

            // eslint-disable-next-line eqeqeq
            if ((fieldValue ?? "") != (originalRow[fieldName] ?? "")) hasChanges = true;
          }
          return newRow;
        }
        return item;
      });

      if (!hasChanges) return;

      pushChange(
        {
          invoiceId: selectedInvoiceData?._id,
          ...(isShiftJob
            ? { jobSlug: currentJobFinal?.jobSlug }
            : { eventUrl: currentEventFinal?.eventUrl }),
          data: {
            [updatedField]: newData,
          },
        },
        {
          invoiceId: selectedInvoiceData?._id,
          ...(isShiftJob
            ? { jobSlug: currentJobFinal?.jobSlug }
            : { eventUrl: currentEventFinal?.eventUrl }),
          data: originalData,
        }
      );
    },
    [
      currentEventFinal?.eventUrl,
      currentJobFinal?.jobSlug,
      isShiftJob,
      isShowingDetailsView,
      pushChange,
      selectedInvoiceData,
    ]
  );

  const handleUpdateInvoice = useCallback(
    async (data, type) => {
      if (type === "customer") {
        pushChange(
          {
            invoiceId: selectedInvoiceData?._id,
            ...(isShiftJob
              ? { jobSlug: currentJobFinal?.jobSlug }
              : { eventUrl: currentEventFinal?.eventUrl }),
            data: {
              customerInformation: {
                ...(selectedInvoiceData?.customerInformation ?? {}),
                name: data?.name,
                address: data?.address,
                city: data?.city,
                state: data?.state,
                zip: data?.zip,
                attn: data?.attn,
              },
            },
          },
          {
            invoiceId: selectedInvoiceData?._id,
            ...(isShiftJob
              ? { jobSlug: currentJobFinal?.jobSlug }
              : { eventUrl: currentEventFinal?.eventUrl }),
            data: {
              customerInformation: {
                ...(selectedInvoiceData?.customerInformation ?? {}),
              },
            },
          }
        );
      }

      if (type === "invoice") {
        pushChange(
          {
            invoiceId: selectedInvoiceData?._id,
            ...(isShiftJob
              ? { jobSlug: currentJobFinal?.jobSlug }
              : { eventUrl: currentEventFinal?.eventUrl }),
            data: {
              invoiceNumber: data?.invoiceNumber,
              // customerNumber: selectedInvoice?.customerNumber,
              invoiceInformation: {
                ...(selectedInvoiceData?.invoiceInformation ?? {}),
                purchaseOrder: data?.purchaseOrder,
                date: data?.date,
                dueDate: data?.dueDate,
                costDeptCode: data?.costDeptCode,
              },
            },
          },
          {
            invoiceId: selectedInvoiceData?._id,
            ...(isShiftJob
              ? { jobSlug: currentJobFinal?.jobSlug }
              : { eventUrl: currentEventFinal?.eventUrl }),
            data: {
              invoiceNumber: selectedInvoiceData?.invoiceNumber,
              // customerNumber: selectedInvoiceData?.customerNumber,
              invoiceInformation: {
                ...(selectedInvoiceData?.invoiceInformation ?? {}),
              },
            },
          }
        );
      }

      if (type === "remittance") {
        pushChange(
          {
            invoiceId: selectedInvoiceData?._id,
            ...(isShiftJob
              ? { jobSlug: currentJobFinal?.jobSlug }
              : { eventUrl: currentEventFinal?.eventUrl }),
            data: {
              remittanceInformation: {
                ...(selectedInvoiceData?.remittanceInformation ?? {}),
                name: data?.name,
                address: data?.address,
                city: data?.city,
                state: data?.state,
                zip: data?.zip,
                attn: data?.attn,
                emailCopy: data?.emailCopy,
                notes: data?.notes,
              },
            },
          },
          {
            invoiceId: selectedInvoiceData?._id,
            ...(isShiftJob
              ? { jobSlug: currentJobFinal?.jobSlug }
              : { eventUrl: currentEventFinal?.eventUrl }),
            data: {
              remittanceInformation: {
                ...(selectedInvoiceData?.remittanceInformation ?? {}),
              },
            },
          }
        );
      }
    },
    [
      currentEventFinal?.eventUrl,
      currentJobFinal?.jobSlug,
      isShiftJob,
      pushChange,
      selectedInvoiceData?._id,
      selectedInvoiceData?.customerInformation,
      selectedInvoiceData?.invoiceInformation,
      selectedInvoiceData?.invoiceNumber,
      selectedInvoiceData?.remittanceInformation,
    ]
  );

  const onAddLine = useCallback(
    ({
      applicantId,
      firstName,
      lastName,
      positionName,
      shiftSlug,
      shiftName,
      totalEmployees,
      partnerSlug,
      email,
      altEmail,
      phone,
      altPhone,
      employeeID,
    }) => {
      const currentData = { ...selectedInvoiceData };
      const updatedField = isShowingDetailsView ? "details" : "summary";

      const originalData = {
        [updatedField]: currentData[updatedField] ?? [],
      };

      const newData = [...(currentData[updatedField] ?? [])];

      // PUSH
      if (isShiftJob) {
        if (isShowingDetailsView) {
          newData.push({
            payRate: 0, // Irrelevant
            billRate: 0,
            positionName,
            eventName: null,
            jobName: currentJobFinal?.title ?? "",
            date: "",
            totalHours: 0,
            totalOvertimeHours: 0,
            totalEmployees: null,
            itemId: new ObjectId().toString(),
            partnerSlug: null,
            salary: "No", // Irrelevant
            earningId: "REG",
            applicantId,
            firstName,
            lastName,
            email,
            altEmail,
            phone,
            altPhone,
            employeeID,
            shiftName,
            shiftSlug,
            timecardId: null, // Irrelevant
          });
        } else {
          newData.push({
            payRate: 0, // Irrelevant
            billRate: 0,
            positionName,
            eventName: null,
            jobName: currentJobFinal?.title ?? "",
            date: "",
            totalHours: 0,
            totalOvertimeHours: 0,
            totalEmployees,
            itemId: new ObjectId().toString(),
          });
        }
      } else if (isShowingDetailsView) {
        newData.push({
          payRate: 0, // Irrelevant
          billRate: 0,
          positionName,
          eventName: currentEventFinal?.eventName ?? "",
          jobName: null,
          date: currentEvent
            ? moment(currentEventFinal?.eventDate)
                .tz(currentEventFinal?.timeZone ?? "America/Chicago")
                .format("MM/DD/YYYY")
            : "",
          totalHours: 0,
          totalOvertimeHours: 0,
          totalEmployees: null,
          itemId: new ObjectId().toString(),
          partnerSlug,
          salary: "No", // Irrelevant
          earningId: "REG", // TODO: ADD TO COLUMN
          applicantId,
          firstName,
          lastName,
          email,
          altEmail,
          phone,
          altPhone,
          employeeID,
          shiftName: null,
          shiftSlug: null,
          timecardId: null,
        });
      } else {
        newData.push({
          payRate: 0, // Irrelevant
          billRate: 0,
          positionName,
          eventName: currentEventFinal?.eventName ?? "",
          jobName: null,
          date: currentEvent
            ? moment(currentEventFinal?.eventDate)
                .tz(currentEventFinal?.timeZone ?? "America/Chicago")
                .format("MM/DD/YYYY")
            : "",
          totalHours: 0,
          totalOvertimeHours: 0,
          totalEmployees,
          itemId: new ObjectId().toString(),
        });
      }

      pushChange(
        {
          invoiceId: selectedInvoiceData?._id,
          ...(isShiftJob
            ? { jobSlug: currentJobFinal?.jobSlug }
            : { eventUrl: currentEventFinal?.eventUrl }),
          data: {
            [updatedField]: newData,
          },
        },
        {
          invoiceId: selectedInvoiceData?._id,
          ...(isShiftJob
            ? { jobSlug: currentJobFinal?.jobSlug }
            : { eventUrl: currentEventFinal?.eventUrl }),
          data: originalData,
        }
      );
    },
    [
      currentEvent,
      currentEventFinal?.eventDate,
      currentEventFinal?.eventName,
      currentEventFinal?.eventUrl,
      currentEventFinal?.timeZone,
      currentJobFinal?.jobSlug,
      currentJobFinal?.title,
      isShiftJob,
      isShowingDetailsView,
      pushChange,
      selectedInvoiceData,
    ]
  );

  const onRemoveLine = useCallback(
    ({ row }) => {
      const currentData = { ...selectedInvoiceData };
      const updatedField = isShowingDetailsView ? "details" : "summary";

      const originalData = {
        [updatedField]: currentData[updatedField] ?? [],
      };

      const newData = (currentData[updatedField] ?? []).filter(
        (item) => item.itemId !== row.itemId
      );

      pushChange(
        {
          invoiceId: selectedInvoiceData?._id,
          ...(isShiftJob
            ? { jobSlug: currentJobFinal?.jobSlug }
            : { eventUrl: currentEventFinal?.eventUrl }),
          data: {
            [updatedField]: newData,
          },
        },
        {
          invoiceId: selectedInvoiceData?._id,
          ...(isShiftJob
            ? { jobSlug: currentJobFinal?.jobSlug }
            : { eventUrl: currentEventFinal?.eventUrl }),
          data: originalData,
        }
      );
    },
    [
      currentEventFinal?.eventUrl,
      currentJobFinal?.jobSlug,
      isShiftJob,
      isShowingDetailsView,
      pushChange,
      selectedInvoiceData,
    ]
  );

  // Submit invoice row modal
  const handleSubmitRowModal = useCallback(
    async ({
      row,
      applicantId,
      firstName,
      lastName,
      positionName,
      shiftSlug,
      shiftName,
      totalEmployees,
      partnerSlug,

      email,
      altEmail,
      phone,
      altPhone,
      employeeID,
    }) => {
      if (row) {
        // Row provided, this is an edit
        onChangeField(
          row,
          [
            "applicantId",
            "firstName",
            "lastName",
            "positionName",
            "shiftSlug",
            "shiftName",
            "totalEmployees",
            "partnerSlug",
          ],
          [
            applicantId,
            firstName,
            lastName,
            positionName,
            shiftSlug,
            shiftName,
            totalEmployees,
            partnerSlug,
          ]
        );
      } else {
        // Row not provided, this is a create
        onAddLine({
          applicantId,
          firstName,
          lastName,
          positionName,
          shiftSlug,
          shiftName,
          totalEmployees,
          partnerSlug,

          email,
          altEmail,
          phone,
          altPhone,
          employeeID,
        });
      }
    },
    [onAddLine, onChangeField]
  );

  const handleRemoveRow = useCallback(
    (row) => {
      onRemoveLine({ row });
    },
    [onRemoveLine]
  );

  // Keyboard shortcut for UNDO
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Check if any input field is focused
      // eslint-disable-next-line prefer-destructuring
      const activeElement = document.activeElement;
      const isInputFocused =
        activeElement &&
        (activeElement.tagName === "INPUT" ||
          activeElement.tagName === "TEXTAREA" ||
          activeElement.contentEditable === "true" ||
          activeElement.isContentEditable);

      // Only handle shortcuts when no input is focused
      if (!isInputFocused && (event.ctrlKey || event.metaKey)) {
        if (event.key === "z" && !event.shiftKey && !isPartnerMembersModalOpen) {
          event.preventDefault();
          handleUndo();
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    // Cleanup
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleUndo, isPartnerMembersModalOpen]);

  const handleSelectRow = useCallback((row) => {
    setSelectedRowForEdit(row);
  }, []);

  const columns = useMemo(() => {
    const result = [
      {
        title: "Date",
        field: "date",
        sortable: false,
        allowedTags: ["fullscreen", "summary", "details", "event"],
        // customCell: (rosterData, field, row) =>
        //   isShiftJob
        //     ? moment(row?.timeIn).format("MM/DD/YY")
        //     : moment(currentEventFinal?.eventDate).format("MM/DD/YY"),
      },
      {
        title: isShiftJob ? "Job" : "Event",
        field: isShiftJob ? "jobName" : "eventName",
        allowedTags: ["fullscreen", "summary", "job", "event"],
      },
      {
        title: "First Name",
        field: "firstName",
        allowedTags: ["fullscreen", "minimized", "details", "job", "event"],
      },
      {
        title: "Last Name",
        field: "lastName",
        allowedTags: ["fullscreen", "minimized", "details", "job", "event"],
      },
      {
        title: "Shift Name",
        field: "shiftSlug",
        customCell: (shiftSlug, field, row) => {
          const jobShifts = currentJobFinal?.shifts ?? [];
          const matchingShift = jobShifts.find((s) => s.slug === shiftSlug);

          if (matchingShift) return matchingShift.shiftName;
          return shiftSlug;
        },
        allowedTags: ["fullscreen", "minimized", "details", "job"],
      },
      {
        title: "Position",
        field: "positionName",
        sortable: true,
        allowedTags: ["fullscreen", "minimized", "summary", "details", "job", "event"],
        customCell: (positionName, field, row) =>
          isShiftJob ? positionName || "Job Staff" : positionName || "Event Staff",
      },
      {
        title: "Qty Staff",
        field: "totalEmployees",
        sortable: false,
        allowedTags: ["fullscreen", "minimized", "summary", "job", "event"],
      },
      {
        title: "Regular Hours",
        field: "totalHours",
        sortable: false,
        width: 130,
        customCell: (rosterData, field, row, i, iSub, isCriticalSubRow, absoluteIndex) => {
          if (!isShowingDetailsView || editLocked) return row?.totalHours ?? "";

          const finalIndex = absoluteIndex;

          return (
            <EditableCellNumber
              amount={row?.totalHours ?? ""}
              setAmount={(val) => onChangeField(row, "totalHours", val ? parseFloat(val) : val)}
              // onValidate={(val) =>
              //   validatePayBillRate(val, row?.rosterData?.payRate ?? "", "billRate")
              // }
              // adornmentSymbol="$"
              onKeyDown={(e) => handleKeyDownField(e, finalIndex, 0)}
              ref={(el) => {
                if (el) {
                  inputRefs.current[getFieldKey(finalIndex, 0)] = el;
                }
              }}
            />
          );
        },
        allowedTags: ["fullscreen", "minimized", "summary", "details", "job", "event"],
      },
      {
        title: "Regular Rate",
        field: "billRate",
        sortable: false,
        width: 130,
        customCell: (rosterData, field, row, i, iSub, isCriticalSubRow, absoluteIndex) => {
          if (!isShowingDetailsView || editLocked) return row?.billRate ?? "";

          const finalIndex = absoluteIndex;

          return (
            <EditableCellNumber
              amount={row?.billRate ?? ""}
              setAmount={(val) => onChangeField(row, "billRate", val ? parseFloat(val) : val)}
              // onValidate={(val) =>
              //   validatePayBillRate(val, row?.rosterData?.payRate ?? "", "billRate")
              // }
              adornmentSymbol="$"
              onKeyDown={(e) => handleKeyDownField(e, finalIndex, 1)}
              ref={(el) => {
                if (el) {
                  inputRefs.current[getFieldKey(finalIndex, 1)] = el;
                }
              }}
            />
          );
        },
        allowedTags: ["fullscreen", "minimized", "summary", "details", "job", "event"],
      },
      {
        title: "OT Hours",
        field: "totalOvertimeHours",
        sortable: false,
        width: 130,
        customCell: (rosterData, field, row, i, iSub, isCriticalSubRow, absoluteIndex) => {
          if (!isShowingDetailsView || editLocked) return row?.totalOvertimeHours ?? "";

          const finalIndex = absoluteIndex;

          return (
            <EditableCellNumber
              amount={row?.totalOvertimeHours ?? ""}
              setAmount={(val) =>
                onChangeField(row, "totalOvertimeHours", val ? parseFloat(val) : val)
              }
              // onValidate={(val) =>
              //   validatePayBillRate(val, row?.rosterData?.payRate ?? "", "billRate")
              // }
              // adornmentSymbol="$"
              onKeyDown={(e) => handleKeyDownField(e, finalIndex, 2)}
              ref={(el) => {
                if (el) {
                  inputRefs.current[getFieldKey(finalIndex, 2)] = el;
                }
              }}
            />
          );
        },
        allowedTags: ["fullscreen", "minimized", "summary", "details", "job", "event"],
      },
      {
        title: "Total Amount",
        field: "totalAmount",
        sortable: false,
        customCell: (rosterData, field, row, i, iSub) => {
          const total =
            (row?.totalHours || 0) * (row?.billRate || 0) +
            (row?.totalOvertimeHours || 0) * (row?.billRate || 0) * 1.5;

          return `$${total.toFixed(2)}`;
        },
        allowedTags: ["fullscreen", "minimized", "summary", "details", "job", "event"],
      },
      {
        title: "Actions",
        field: "applicantId",
        width: 200,
        sortable: false,
        customCell: (rowId, field, row, ind, iSub) => (
          <InvoiceEditorTableActions
            id={rowId}
            row={row}
            setNavigateUrl={navigateWithBack}
            isShiftJob={isShiftJob}
            onToggleSubList={() => {}}
            editLocked={editLocked}
            showInfoButton={isShowingDetailsView}
            onSelectRow={handleSelectRow}
            onDeleteRow={handleRemoveRow}
          />
        ),
        allowedTags: ["fullscreen", "minimized", "details", "job", "event"],
      },
    ];

    return result.flatMap((column) => {
      if (fullscreen && !column.allowedTags.includes("fullscreen")) {
        return [];
      }
      if (!fullscreen && !column.allowedTags.includes("minimized")) {
        return [];
      }
      if (isShiftJob && !column.allowedTags.includes("job")) {
        return [];
      }
      if (!isShiftJob && !column.allowedTags.includes("event")) {
        return [];
      }
      if (isShowingDetailsView && !column.allowedTags.includes("details")) {
        return [];
      }
      if (!isShowingDetailsView && !column.allowedTags.includes("summary")) {
        return [];
      }
      return [column];
    });
  }, [
    currentJobFinal?.shifts,
    editLocked,
    fullscreen,
    getFieldKey,
    handleKeyDownField,
    handleRemoveRow,
    handleSelectRow,
    isShiftJob,
    isShowingDetailsView,
    navigateWithBack,
    onChangeField,
  ]);

  const dataArray = useMemo(
    () => (isShowingDetailsView ? selectedInvoiceData?.details ?? [] : calculatedSummary ?? []),
    [calculatedSummary, isShowingDetailsView, selectedInvoiceData?.details]
  );

  const totals = useMemo(() => {
    let subtotal = 0;
    // eslint-disable-next-line prefer-const
    let tax = 0;
    // eslint-disable-next-line prefer-const
    let discount = 0;
    let total = 0;
    if (!dataArray || !dataArray.length) return { subtotal, tax, discount, total };

    // Burden Rate
    const burdenInfo = burdenRate?.data?.burdenInfo;
    const calculatedBurdenRate =
      (burdenInfo?.payrollTaxes ?? 0) +
      (burdenInfo?.futa ?? 0) +
      (burdenInfo?.suta ?? 0) +
      (burdenInfo?.workersCompensation ?? 0) +
      (burdenInfo?.benefits ?? 0) +
      (burdenInfo?.administrativeCosts ?? 0);

    dataArray.forEach((item) => {
      const itemTotal =
        (item?.totalHours || 0) * (item?.billRate || 0) +
        (item?.totalOvertimeHours || 0) * (item?.billRate || 0) * 1.5;
      subtotal += itemTotal;
      total += itemTotal;
    });

    // tax = subtotal * calculatedBurdenRate;
    total = subtotal + tax;

    return { subtotal, tax, discount, total };
  }, [burdenRate?.data?.burdenInfo, dataArray]);

  return (
    <>
      <Grid container spacing={2} sx={{ marginTop: "4px" }}>
        <Grid item xs={12} xl={fullscreen ? 10 : 12}>
          <Card
            className={classes.box}
            sx={
              fullscreen
                ? undefined
                : {
                    height: 850,
                  }
            }
          >
            <Grid
              container
              justifyContent="space-between"
              alignItems="flex-start"
              flexWrap="nowrap"
            >
              <MDBox>
                <Grid
                  container
                  spacing={2}
                  alignItems="center"
                  justifyContent="space-between"
                  py={1}
                >
                  <Grid container item justifyContent="space-between">
                    <MDBox display="flex">
                      <MDBox
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        width="4rem"
                        height="4rem"
                        variant="gradient"
                        bgColor="violet"
                        color="white"
                        borderRadius="xl"
                        ml={3}
                        mt={-4}
                      >
                        <Icon fontSize="large">account_balance</Icon>
                      </MDBox>
                      <MDTypography variant="h5" color="dark" sx={{ ml: 2 }}>
                        {title}
                      </MDTypography>
                    </MDBox>
                  </Grid>
                  {invoiceStatus === "Void" && (
                    <Grid container item justifyContent="space-between">
                      <MDBox sx={{ ml: 3, maxWidth: fullscreen ? "unset" : "600px" }}>
                        <Grid container item alignItems="center" flexWrap="nowrap">
                          <PriorityHigh
                            color="white"
                            fontSize="medium"
                            sx={{
                              mr: 1,
                              padding: 0.3,
                              backgroundColor: "error.main",
                              borderRadius: "50%",
                            }}
                          />
                          <MDTypography variant="body" color="error">
                            Void
                          </MDTypography>
                        </Grid>
                      </MDBox>
                    </Grid>
                  )}
                </Grid>
                {!isForInvoicesScreen && (
                  <Grid container spacing={1} alignItems="center">
                    <Grid item>
                      <MDBox ml={3} width="300px">
                        <Autocomplete
                          value={
                            selectedInvoiceFinal
                              ? invoiceDropdownOptions.find(
                                  (op) => op.value === selectedInvoiceFinal
                                )
                              : ""
                          }
                          options={invoiceDropdownOptions}
                          autoSelect
                          onChange={(e, v) => {
                            setCommandManager(() => new CommandManager());
                            if (v) setSelectedInvoice(v?.value);
                            else setSelectedInvoice("");
                          }}
                          sx={{ maxHeight: "44px", width: "100%" }}
                          disableClearable
                          disabled={isLoadingInvoiceList || !payrollSubmitted}
                          renderInput={(params) => (
                            <CustomTextField
                              variant="outlined"
                              showError
                              {...params}
                              InputLabelProps={{ shrink: true }}
                              sx={{
                                "& input": {
                                  padding: "3px !important",
                                },
                              }}
                            />
                          )}
                        />
                      </MDBox>
                    </Grid>
                    <Grid item>
                      <MDBox
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "flex-end",
                        }}
                      >
                        <MDButton
                          onClick={() => {
                            handleCreate();
                          }}
                          startIcon={<AddCircleRounded />}
                          color="info"
                          variant="contained"
                          disabled={isLoadingInvoiceList || !payrollSubmitted}
                          alignItems="center"
                        >
                          Create Invoice
                        </MDButton>
                      </MDBox>
                    </Grid>
                  </Grid>
                )}
              </MDBox>
              <MDBox sx={{ paddingTop: 1, paddingRight: 2 }}>
                <Stack direction="row" sx={{ display: "flex", alignItems: "center" }}>
                  {updateDataMutation.isLoading && fullscreen && (
                    <>
                      <MDTypography variant="text" color="dark" sx={{ mr: 2 }}>
                        Saving
                      </MDTypography>
                      <CircularProgress color="secondary" size={20} sx={{ marginRight: 3 }} />
                    </>
                  )}
                  <MDBox display="flex" justifyContent="center" alignItems="center" mr={2}>
                    <ToggleButtonGroup
                      color="info"
                      orientation="horizontal"
                      value={isShowingDetailsView}
                      exclusive
                      onChange={(v) => {
                        setShowingDetailsView(v.target.value === "true");
                        refetchPayrollStatus();
                      }}
                    >
                      <ToggleButton value={false} aria-label="list">
                        Summary
                      </ToggleButton>
                      <ToggleButton value aria-label="module">
                        Details
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </MDBox>
                  {fullscreen && !isForInvoicesScreen && (
                    <MDBox display="flex" justifyContent="center" alignItems="center" mr={2}>
                      <SplitPanelSelector
                        panelOptions={localPanelOptions}
                        selectedPanel="invoice"
                        isEdit
                      />
                    </MDBox>
                  )}
                  <Tooltip title="Reset Invoice">
                    <span>
                      <IconButton
                        color="error"
                        onClick={() => setResetConfirmationModalOpen(true)}
                        disabled={isLoadingInvoiceList || editLocked || !selectedInvoiceFinal}
                      >
                        <Restore fontSize="large" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Delete Invoice">
                    <span>
                      <IconButton
                        color="error"
                        onClick={() => setDeleteConfirmationModalOpen(true)}
                        disabled={isLoadingInvoiceList || editLocked || !selectedInvoiceFinal}
                      >
                        <Delete fontSize="large" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Undo">
                    <span>
                      <IconButton
                        color="info"
                        onClick={handleUndo}
                        disabled={isLoadingInvoiceList || editLocked || !selectedInvoiceFinal}
                      >
                        <Undo fontSize="large" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  {!fullscreen && !isForInvoicesScreen && (
                    <Tooltip title="Go Fullscreen">
                      <span>
                        <IconButton color="info" onClick={toggleFullscreen}>
                          <AspectRatio fontSize="large" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                  {fullscreen && !isForInvoicesScreen && (
                    <Tooltip title="Minimize">
                      <span>
                        <IconButton color="info" onClick={minimize}>
                          <CloseFullscreen fontSize="large" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                </Stack>
                <Grid item>
                  <MDBox
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-end",
                    }}
                  >
                    {updateDataMutation.isLoading && !fullscreen && (
                      <>
                        <MDTypography variant="text" color="dark" sx={{ mr: 2 }}>
                          Saving
                        </MDTypography>
                        <CircularProgress color="secondary" size={20} sx={{ marginRight: 3 }} />
                      </>
                    )}
                    <MDButton
                      onClick={() => {
                        setSelectedRowForEdit({});
                      }}
                      startIcon={<AddCircleRounded />}
                      color="info"
                      variant="contained"
                      disabled={
                        isLoadingInvoiceList ||
                        editLocked ||
                        !selectedInvoiceFinal ||
                        !isShowingDetailsView
                      }
                      alignItems="center"
                    >
                      Add Line
                    </MDButton>
                  </MDBox>
                </Grid>
              </MDBox>
            </Grid>
            <MDBox
              pt={2}
              pb={2}
              pl={!fullscreen ? 2 : 0}
              pr={!fullscreen ? 2 : 0}
              sx={{ display: "flex", flexGrow: 1, alignItems: "stretch", flexDirection: "column" }}
            >
              {!payrollSubmitted && (
                <MDBox sx={{ mt: 1, mb: 4 }}>
                  <MDTypography variant="h4" color="dark" sx={{ ml: 2, textAlign: "center" }}>
                    Payroll not submitted
                  </MDTypography>
                  <MDTypography variant="h5" color="secondary" sx={{ ml: 2, textAlign: "center" }}>
                    {`Please submit the ${
                      isShiftJob ? "Job" : "Event"
                    } in the Payroll Editor first and then try again.`}
                  </MDTypography>
                </MDBox>
              )}
              {!!payrollSubmitted && !selectedInvoiceFinal && (
                <MDBox sx={{ mt: 1, mb: 4 }}>
                  <MDTypography variant="h4" color="dark" sx={{ ml: 2, textAlign: "center" }}>
                    No Invoice Selected
                  </MDTypography>
                  <MDTypography variant="h5" color="secondary" sx={{ ml: 2, textAlign: "center" }}>
                    Please select an existing invoice or create one.
                  </MDTypography>
                </MDBox>
              )}
              {!!payrollSubmitted && selectedInvoiceFinal && (
                <DataTableWithSubrows
                  columns={columns}
                  data={{ data: dataArray, count: dataArray.length }}
                  page={page}
                  limit={fullscreen ? limit : defaultMinimizedPageSize}
                  setPage={setPage}
                  setLimit={setLimit}
                  isLoading={isLoadingInvoiceList}
                  // onRowClick={isNotClientOrEventAdmin ? (row, e) => handleRowClick(row, e) : () => {}}
                  order={order}
                  orderBy={orderBy}
                  toggleSort={toggleSort}
                  fetchAll
                  defaultSort="lastName:asc"
                  idField="_id"
                  {...(fullscreen ? {} : { height: 570 })}
                  hidePageSizeSelector={!fullscreen}
                  stickyHeader={!fullscreen}
                  openIndexes={{}}
                />
              )}
            </MDBox>
            <MDBox sx={{ paddingLeft: 0 }}>
              <Grid
                container
                direction="row"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  // backgroundColor: "rgba(0, 0, 0, 0.04)",
                  paddingTop: "8px",
                  paddingBottom: "16px",
                  paddingLeft: "16px",
                  paddingRight: "16px",
                  borderTop: "2px solid rgba(0, 0, 0, 0.08)",
                }}
              >
                <Grid item xs={12} md={2.2}>
                  <MDBox
                    sx={{ display: "flex", alignItems: "center", justifyContent: "flex-start" }}
                  >
                    <MDButton
                      onClick={() => {
                        setInvoicePreviewOpen(true);
                        refetchPayrollStatus();
                      }}
                      color="info"
                      variant="contained"
                      disabled={isLoadingInvoiceList || !payrollSubmitted || !selectedInvoiceFinal}
                    >
                      Preview
                    </MDButton>
                  </MDBox>
                </Grid>
                <Grid item xs={6} md={2.2}>
                  <MDTypography variant="body2" color="dark" sx={{ ml: 2, textAlign: "center" }}>
                    Subtotal
                  </MDTypography>
                  <MDTypography
                    variant="body2"
                    color="dark"
                    sx={{ ml: 2, textAlign: "center", fontWeight: "bold" }}
                  >
                    {`$${(totals?.subtotal ?? 0).toFixed(2)}`}
                  </MDTypography>
                </Grid>
                <Grid item xs={6} md={2.2}>
                  <MDTypography variant="body2" color="dark" sx={{ ml: 2, textAlign: "center" }}>
                    Tax
                  </MDTypography>
                  <MDTypography
                    variant="body2"
                    color="dark"
                    sx={{ ml: 2, textAlign: "center", fontWeight: "bold" }}
                  >
                    {`$${(totals?.tax ?? 0).toFixed(2)}`}
                  </MDTypography>
                </Grid>
                <Grid item xs={6} md={2.2}>
                  <MDTypography variant="body2" color="dark" sx={{ ml: 2, textAlign: "center" }}>
                    Discount
                  </MDTypography>
                  <MDTypography
                    variant="body2"
                    color="dark"
                    sx={{ ml: 2, textAlign: "center", fontWeight: "bold" }}
                  >
                    {`$${(totals?.discount ?? 0).toFixed(2)}`}
                  </MDTypography>
                </Grid>
                <Grid item xs={6} md={2.2}>
                  <MDTypography variant="body2" color="dark" sx={{ ml: 2, textAlign: "center" }}>
                    Total
                  </MDTypography>
                  <MDTypography
                    variant="body2"
                    color="dark"
                    sx={{ ml: 2, textAlign: "center", fontWeight: "bold" }}
                  >
                    {`$${(totals?.total ?? 0).toFixed(2)}`}
                  </MDTypography>
                </Grid>
              </Grid>
            </MDBox>
          </Card>
        </Grid>
        {fullscreen && (
          <Grid container item xs={12} xl={2} sx={{}} spacing={2}>
            <Grid item xs="6" md="4" xl="12">
              <Card
                sx={{
                  padding: 2,
                }}
              >
                <MDBox
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1,
                  }}
                >
                  <MDTypography variant="h5" color="dark">
                    Customer Information
                  </MDTypography>
                  <IconButton
                    color="info"
                    size="small"
                    disabled={isLoadingInvoiceList || editLocked || !selectedInvoiceFinal}
                    onClick={() => setEditCustomerInfoModalOpen(true)}
                  >
                    <Edit />
                  </IconButton>
                </MDBox>
                <MDTypography variant="body2" color="dark" sx={{ mb: 1, fontWeight: "bold" }}>
                  {selectedInvoiceData?.customerInformation?.name ?? "-"}
                </MDTypography>
                <MDTypography variant="body2" color="dark" sx={{ mb: 1 }}>
                  {selectedInvoiceData?.customerInformation?.address ?? "-"}
                </MDTypography>
                <MDTypography variant="body2" color="dark" sx={{ mb: 1 }}>
                  {`${selectedInvoiceData?.customerInformation?.city ?? "-"}, ${
                    selectedInvoiceData?.customerInformation?.state ?? "-"
                  } ${selectedInvoiceData?.customerInformation?.zip ?? ""}`}
                </MDTypography>
                <MDTypography variant="body2" color="dark" sx={{}}>
                  <span style={{ fontWeight: "bold" }}>Attn:</span>{" "}
                  {selectedInvoiceData?.customerInformation?.attn}
                </MDTypography>
              </Card>
            </Grid>

            <Grid item xs="6" md="4" xl="12">
              <Card
                sx={{
                  padding: 2,
                }}
              >
                <MDBox
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1,
                  }}
                >
                  <MDTypography variant="h5" color="dark" sx={{ mb: 1 }}>
                    Invoice Information
                  </MDTypography>
                  <IconButton
                    color="info"
                    size="small"
                    disabled={isLoadingInvoiceList || editLocked || !selectedInvoiceFinal}
                    onClick={() => setEditInvoiceInfoModalOpen(true)}
                  >
                    <Edit />
                  </IconButton>
                </MDBox>
                <MDTypography variant="body2" color="dark" sx={{ mb: 1 }}>
                  <span style={{ fontWeight: "bold" }}>Invoice Number:</span>{" "}
                  {selectedInvoiceData?.invoiceNumber?.toString()?.padStart(8, "0") ?? "-"}
                </MDTypography>
                <MDTypography variant="body2" color="dark" sx={{ mb: 1 }}>
                  <span style={{ fontWeight: "bold" }}>Customer Number:</span>{" "}
                  {selectedInvoiceData?.customerNumber ?? "-"}
                </MDTypography>
                <MDTypography variant="body2" color="dark" sx={{ mb: 1 }}>
                  <span style={{ fontWeight: "bold" }}>Purchase Order:</span>{" "}
                  {selectedInvoiceData?.invoiceInformation?.purchaseOrder ?? "-"}
                </MDTypography>
                <MDTypography variant="body2" color="dark" sx={{ mb: 1 }}>
                  <span style={{ fontWeight: "bold" }}>Invoice Date:</span>{" "}
                  {selectedInvoiceData?.invoiceInformation?.date ?? "-"}
                </MDTypography>
                <MDTypography variant="body2" color="dark" sx={{ mb: 1 }}>
                  <span style={{ fontWeight: "bold" }}>Due Date:</span>{" "}
                  {selectedInvoiceData?.invoiceInformation?.dueDate ?? "-"}
                </MDTypography>
                <MDTypography variant="body2" color="dark" sx={{ mb: 1 }}>
                  <span style={{ fontWeight: "bold" }}>Cost/Dept. Code:</span>{" "}
                  {selectedInvoiceData?.invoiceInformation?.costDeptCode ?? "-"}
                </MDTypography>
              </Card>
            </Grid>

            <Grid item xs="6" md="4" xl="12">
              <Card
                sx={{
                  padding: 2,
                }}
              >
                <MDBox
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1,
                  }}
                >
                  <MDTypography variant="h5" color="dark" sx={{ mb: 1 }}>
                    Remittance Information
                  </MDTypography>
                  <IconButton
                    color="info"
                    size="small"
                    disabled={isLoadingInvoiceList || editLocked || !selectedInvoiceFinal}
                    onClick={() => setEditRemittanceInfoModalOpen(true)}
                  >
                    <Edit />
                  </IconButton>
                </MDBox>
                <MDTypography variant="body2" color="dark" sx={{ mb: 1, fontWeight: "bold" }}>
                  {selectedInvoiceData?.remittanceInformation?.name ?? "-"}
                </MDTypography>
                <MDTypography variant="body2" color="dark" sx={{ mb: 1 }}>
                  {selectedInvoiceData?.remittanceInformation?.address ?? "-"}
                </MDTypography>
                <MDTypography variant="body2" color="dark" sx={{ mb: 1 }}>
                  {`${selectedInvoiceData?.remittanceInformation?.city ?? "-"}, ${
                    selectedInvoiceData?.remittanceInformation?.state ?? "-"
                  } ${selectedInvoiceData?.remittanceInformation?.zip ?? ""}`}
                </MDTypography>
                <MDTypography variant="body2" color="dark" sx={{ mb: 1 }}>
                  <span style={{ fontWeight: "bold" }}>Attn:</span>{" "}
                  {selectedInvoiceData?.remittanceInformation?.attn}
                </MDTypography>
                <MDTypography variant="body2" color="dark" sx={{ mb: 1 }}>
                  <span style={{ fontWeight: "bold" }}>Email Copy:</span>{" "}
                  {selectedInvoiceData?.remittanceInformation?.emailCopy}
                </MDTypography>
                <MDTypography variant="body2" color="dark" sx={{ whiteSpace: "pre-line" }}>
                  <span style={{ fontWeight: "bold" }}>Notes:</span>{" "}
                  {selectedInvoiceData?.remittanceInformation?.notes}
                </MDTypography>
              </Card>
            </Grid>
          </Grid>
        )}
      </Grid>
      {isResetConfirmationModalOpen && (
        <ConfirmationModal
          isOpen={isResetConfirmationModalOpen}
          onClose={() => {
            setResetConfirmationModalOpen(false);
          }}
          title="Reset Data"
          body="Are you sure you want to reset the invoice? This will restore the values saved in the submitted payroll. This action cannot be undone."
          onConfirm={(e) => {
            e.stopPropagation();
            handleReset();
            setResetConfirmationModalOpen(false);
          }}
        />
      )}
      {isDeleteConfirmationModalOpen && (
        <ConfirmationModal
          isOpen={isDeleteConfirmationModalOpen}
          onClose={() => {
            setDeleteConfirmationModalOpen(false);
          }}
          title="Delete Invoice"
          body="Are you sure you want to delete the invoice? This action cannot be undone."
          onConfirm={(e) => {
            e.stopPropagation();
            handleDelete();
            setDeleteConfirmationModalOpen(false);
          }}
        />
      )}
      {!!selectedRowForEdit && (
        <AddInvoiceRowModal
          open={!!selectedRowForEdit}
          setOpen={() => {
            setSelectedRowForEdit(null);
          }}
          onAddRow={handleSubmitRowModal}
          row={selectedRowForEdit}
          isShiftJob={isShiftJob}
          isDetails={isShowingDetailsView}
          employees={invoiceOptions?.data?.employees}
          shifts={invoiceOptions?.data?.shifts}
          positions={invoiceOptions?.data?.positions}
        />
      )}
      {isEditCustomerInfoModalOpen && (
        <EditInvoiceCustomerDataModal
          open={isEditCustomerInfoModalOpen}
          setOpen={setEditCustomerInfoModalOpen}
          onSubmit={(values) => handleUpdateInvoice(values, "customer")}
          customerInformation={selectedInvoiceData?.customerInformation ?? {}}
          type="customer"
        />
      )}
      {isEditInvoiceInfoModalOpen && (
        <EditInvoiceCustomerDataModal
          open={isEditInvoiceInfoModalOpen}
          setOpen={setEditInvoiceInfoModalOpen}
          onSubmit={(values) => handleUpdateInvoice(values, "invoice")}
          customerInformation={{
            invoiceNumber: selectedInvoiceData?.invoiceNumber,
            customerNumber: selectedInvoiceData?.customerNumber,
            ...(selectedInvoiceData?.invoiceInformation ?? {}),
          }}
          type="invoice"
        />
      )}
      {isEditRemittanceInfoModalOpen && (
        <EditInvoiceCustomerDataModal
          open={isEditRemittanceInfoModalOpen}
          setOpen={setEditRemittanceInfoModalOpen}
          onSubmit={(values) => handleUpdateInvoice(values, "remittance")}
          customerInformation={selectedInvoiceData?.remittanceInformation ?? {}}
          type="remittance"
        />
      )}
      {invoicePreviewOpen && (
        <InvoiceEditorPreviewModal
          open={invoicePreviewOpen}
          setOpen={setInvoicePreviewOpen}
          detailRows={selectedInvoiceData?.details ?? []}
          summaryRows={calculatedSummary ?? []}
          setInvoiceFile={setInvoiceFile}
          currentEvent={currentEventFinal}
          currentJob={currentJobFinal}
          invoiceNumber={
            selectedInvoiceData?.invoiceNumber
              ? selectedInvoiceData?.invoiceNumber.toString().padStart(8, "0")
              : null
          }
          startDate={selectedInvoiceData?.startDate}
          from={{
            name: selectedInvoiceData?.remittanceInformation?.name,
            address: selectedInvoiceData?.remittanceInformation?.address,
            city: selectedInvoiceData?.remittanceInformation?.city,
            state: selectedInvoiceData?.remittanceInformation?.state,
            zip: selectedInvoiceData?.remittanceInformation?.zip,
            notes: selectedInvoiceData?.remittanceInformation?.notes,
            attn: selectedInvoiceData?.remittanceInformation?.attn,
          }}
          to={{
            name: selectedInvoiceData?.customerInformation?.name,
            address: selectedInvoiceData?.customerInformation?.address,
            city: selectedInvoiceData?.customerInformation?.city,
            state: selectedInvoiceData?.customerInformation?.state,
            zip: selectedInvoiceData?.customerInformation?.zip,
            attn: selectedInvoiceData?.customerInformation?.attn,
          }}
          invoiceInformation={{
            date: selectedInvoiceData?.invoiceInformation?.date,
            dueDate: selectedInvoiceData?.invoiceInformation?.dueDate,
            purchaseOrder: selectedInvoiceData?.invoiceInformation?.purchaseOrder,
          }}
          isShiftJob={isShiftJob}
          burdenRate={burdenRate}
          isVoid={invoiceStatus === "Void"}
          invoiceId={selectedInvoiceData?._id}
        />
      )}
      {!!invoiceFile && (
        <SendInvoiceMessageModal
          open={!!invoiceFile}
          setOpen={() => setInvoiceFile(null)}
          parent="Venue"
          messageContext="Venue"
          attachmentContext="Venue"
          venueCustomer={venueCustomer}
          invoiceFile={invoiceFile}
          // venueId={venueId}
          // companyId={companyId}
        />
      )}
    </>
  );
};

// Setting default values for the props
InvoiceEditorTable.defaultProps = {
  // currentVenue: {},
  title: "Applicants",
};

// Typechecking props
InvoiceEditorTable.propTypes = {
  // currentVenue: PropTypes.object,
  title: PropTypes.string,
};

export default InvoiceEditorTable;
